import { ITeamMemberDisplay } from "../../common/api_interfaces/project/ITeamDisplay";
import { IGetAllTaskPerson, IPerson } from "../../common/api_interfaces/task/IGetAllTaskPerson";
import { fullNameFormat } from "../Inicials";

export class TaskAssignmentManager {
    private team: ITeamMemberDisplay[] | undefined;
    private executor: IPerson | undefined | null;
    private group: IPerson[] | undefined;
    private allPerson: IGetAllTaskPerson | undefined;

    /**
     * Конструктор класса для управления назначением исполнителей и групп
     * @param team - Список всех членов команды
     * @param executor - Текущий исполнитель задачи
     * @param group - Текущая группа соисполнителей
     * @param allPerson - Объект с информацией об исходном исполнителе
    */
    constructor(
        team: ITeamMemberDisplay[] | undefined,
        executor: IPerson | undefined | null,
        group: IPerson[] | undefined,
        allPerson: IGetAllTaskPerson | undefined
    ) {
        this.team = team;
        this.executor = executor;
        this.group = group;
        this.allPerson = allPerson;
    }

    /**
     * Получает отфильтрованный список пользователей для выбора исполнителя
     * Исключает текущего исполнителя из списка доступных
     * @returns Массив объектов с данными пользователей для выбора
     */
    getFilteredChoiceExecutor(): {
        id: number;
        role: string;
        name: string;
        surname: string;
        middleName: string;
        photoId: number | undefined;
    }[] {
        return this.team?.filter(xx => xx.id !== this.executor?.id)
            .map(person => ({
                id: person.id,
                role: person.position,
                name: fullNameFormat(person, "s N M"),
                surname: "",
                middleName: "",
                photoId: person.photoId,
            })) || [];
    }

    /**
     * Получает отфильтрованный список пользователей для добавления в группу
     * Исключает текущего исполнителя и уже добавленных в группу пользователей
     * @returns Массив объектов с данными пользователей для добавления в группу
     */
    getFilteredChoiceGroup(): {
        id: number;
        role: string;
        name: string;
        surname: string;
        middleName: string;
        photoId: number | undefined;
    }[] {
        const filteredExecutor = this.getFilteredChoiceExecutor();
        return filteredExecutor?.filter(person =>
            !this.group?.some(groupPerson => groupPerson.id === person.id)
        ).map(person => ({
            id: person.id,
            role: person.role,
            name: person.name,
            surname: person.surname,
            middleName: person.middleName,
            photoId: person.photoId,
        })) || [];
    }

    /**
     * Очищает текущего исполнителя задачи
     * @returns Объект с undefined значением исполнителя
     */
    clearResponsible(): { executor: undefined } {
        return { executor: undefined };
    }

    /**
     * Обрабатывает выбор нового исполнителя задачи
     * Удаляет нового исполнителя из группы (если он там был)
     * Добавляет предыдущего исполнителя в группу (при необходимости)
     * @param userId - ID пользователя для назначения исполнителем или null для сброса
     * @returns Объект с новым исполнителем и обновленной группой
     * @throws Error если указанный пользователь не найден
     */
    handleSelectExecutor(userId: number | null): {
        executor: IPerson | null | undefined;
        group: IPerson[] | undefined;
    } {
        // Если тот же пользователь, не трогаем
        if (userId === this.executor?.id) {
            return { executor: this.executor, group: this.group };
        }

        const person: ITeamMemberDisplay | undefined | null = userId === null ? null : this.team?.find((p) => p.id === userId);

        if (person === undefined) {
            throw new Error('Указанный пользователь не найден');
        }

        let newGroup: IPerson[] | undefined = this.group ? [...this.group] : undefined;

        // Если в группе подписчиков, удаляем оттуда
        if (person && this.group?.find(xx => xx.id === person?.id)) {
            newGroup = this.group.filter(xx => xx.id !== person?.id);
            // Если группа стала пустой, устанавливаем undefined
            if (newGroup.length === 0) {
                newGroup = undefined;
            }
        }

        // Если исполнителя убрали, то добавляем его в группу
        if (this.executor && this.executor?.id !== userId && this.executor?.id === this.allPerson?.executor?.id) {
            if (newGroup) {
                if (!newGroup.find(xx => xx.id === this.executor!.id)) {
                    newGroup.push(this.executor);
                }
            } else {
                newGroup = [this.executor];
            }
        }

        return {
            executor: person,
            group: newGroup
        };
    }

    /**
     * Добавляет пользователя в группу соисполнителей
     * Если добавляется текущий исполнитель - очищает его из исполнителей
     * @param userId - ID пользователя для добавления в группу
     * @returns Объект с обновленным исполнителем и группой
     */
    handleSelectGroup(userId: number): {
        executor: IPerson | undefined | null;
        group: IPerson[] | undefined;
    } {
        const person = this.team?.find((p) => p.id === userId);

        let newExecutor = this.executor;
        let newGroup: IPerson[] | undefined = undefined;

        if (person) {
            // Если добавляем текущего исполнителя в группу - очищаем исполнителя
            if (person.id === this.executor?.id) {
                newExecutor = null;
            }

            newGroup = [
                ...(this.group || []),
                {
                    ...person,
                    middlename: person.middlename,
                    photoId: person.photoId,
                }
            ];
        }

        return {
            executor: newExecutor,
            group: newGroup
        };
    }

    /**
     * Удаляет пользователя из группы соисполнителей
     * @param userId - ID пользователя для удаления из группы
     * @returns Объект с обновленной группой (undefined если группа пустая)
     */
    handleRemoveGroup(userId: number): { group: IPerson[] | undefined } {
        let newGroup: IPerson[] | undefined = undefined;

        if (this.group?.find(xx => xx.id === userId)) {
            newGroup = this.group.filter(xx => xx.id !== userId);
            if (newGroup.length === 0) {
                newGroup = [];
            }
        }

        return { group: newGroup };
    }

    /**
     * Обновляет внутренние данные класса
     * @param team - Новый список членов команды
     * @param executor - Новый текущий исполнитель
     * @param group - Новая группа соисполнителей
     * @param allPerson - Новый объект с информацией об исходном исполнителе
     */
    updateData(
        team: ITeamMemberDisplay[] | undefined,
        executor: IPerson | undefined | null,
        group: IPerson[] | undefined,
        allPerson: IGetAllTaskPerson | undefined
    ) {
        this.team = team;
        this.executor = executor;
        this.group = group;
        this.allPerson = allPerson;
    }
}